import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiPlus, FiSettings, FiActivity, FiGlobe, FiLoader, FiRefreshCw, FiPauseCircle, FiPlayCircle, FiX, FiCheck, FiBox } = FiIcons;
import { useNavigate } from 'react-router-dom';

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const CreateAppModal = ({ clients, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ app_name: '', client_id: '', allowed_origins: '', status: 'active' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.app_name || !formData.client_id) return showToast('App Name and Client are required', 'error');
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create_app', payload: formData })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      onSave(result.data);
    } catch (err) { showToast(err.message, 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2"><SafeIcon icon={FiBox} /> Create Application</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded"><SafeIcon icon={FiX} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">App Name *</label>
            <input required type="text" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:bg-white focus:border-blue-500" value={formData.app_name} onChange={e => setFormData({ ...formData, app_name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Linked Client *</label>
            <select required className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white" value={formData.client_id} onChange={e => setFormData({ ...formData, client_id: e.target.value })}>
              <option value="">Select Client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.client_name || c.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Allowed Origins (CSV)</label>
            <input type="text" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono" placeholder="https://example.com" value={formData.allowed_origins} onChange={e => setFormData({ ...formData, allowed_origins: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-bold text-slate-500">Cancel</button>
            <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 flex items-center gap-2">
              {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheck} />} Create App
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Apps = () => {
  const [apps, setApps] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resApps, resClients] = await Promise.all([
        fetch('/.netlify/functions/gateway', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list_apps' }) }).then(r => r.json()),
        fetch('/.netlify/functions/gateway', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list_clients' }) }).then(r => r.json())
      ]);
      setApps(resApps.success ? resApps.data : []);
      setClients(resClients.success ? resClients.data : []);
    } finally { setLoading(false); }
  };

  const handleUpdateStatus = async (id, currentStatus) => {
    setUpdatingId(id);
    const newStatus = (currentStatus === 'active' || currentStatus === 'enabled') ? 'suspended' : 'active';
    try {
      const res = await fetch('/.netlify/functions/gateway', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'update_app_status', payload: { app_id: id, status: newStatus } }) }).then(r => r.json());
      if (!res.success) throw new Error(res.message);
      showToast(`App ${newStatus === 'active' ? 'activated' : 'suspended'}`);
      fetchData();
    } catch (err) { showToast(err.message, 'error'); }
    finally { setUpdatingId(null); }
  };

  const handleConfigClick = () => {
    showToast("Opening app configuration. Detailed app-level binding is not connected yet.", "info");
    navigate('/app-config');
  };

  const handleLogsClick = () => {
    showToast("Opening logs. App-specific filtering is not connected yet.", "info");
    navigate('/logs');
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <div className="space-y-6 animate-in slide-in-from-right-4 duration-500 pb-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Applications</h2>
          <p className="text-sm text-slate-500">Service endpoints and origin security.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="p-2.5 bg-white border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 transition-all shadow-sm">
            <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowAddModal(true)} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 shadow-md flex items-center gap-2">
            <SafeIcon icon={FiPlus} /> Create Application
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 relative min-h-[300px]">
        {loading && (
          <div className="absolute inset-0 bg-slate-50/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
            <SafeIcon icon={FiLoader} className="text-3xl text-blue-600 animate-spin" />
          </div>
        )}
        {apps.map(app => (
          <div key={app.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all group flex flex-col">
            <div className="p-5 flex-1">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-900 flex items-center justify-center">
                    <SafeIcon icon={FiActivity} className="text-xl" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-slate-800 truncate">{app.app_name || app.name}</h3>
                    <p className="text-xs text-slate-500 truncate">{clients.find(c => c.id === app.client_id)?.client_name || 'Unlinked App'}</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${app.status === 'active' || app.status === 'enabled' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                  {app.status}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs mt-4">
                <span className="text-slate-500 flex items-center gap-1.5 font-medium"><SafeIcon icon={FiGlobe} className="text-slate-400" /> Origins</span>
                <span className="text-slate-800 font-mono text-[10px] truncate max-w-[150px]">{app.allowed_origins || 'All (*)'}</span>
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 grid grid-cols-3 gap-2 bg-slate-50/50 rounded-b-2xl">
              <button onClick={handleConfigClick} className="py-2 bg-slate-900 text-white text-[10px] font-bold rounded-lg flex items-center justify-center gap-1 hover:bg-slate-800 transition-all">
                <SafeIcon icon={FiSettings} /> Config
              </button>
              <button onClick={handleLogsClick} className="py-2 bg-white border border-slate-200 text-slate-700 text-[10px] font-bold rounded-lg flex items-center justify-center gap-1 hover:bg-slate-50 transition-all">
                <SafeIcon icon={FiActivity} /> Logs
              </button>
              <button onClick={() => handleUpdateStatus(app.id, app.status)} disabled={updatingId === app.id} className={`py-2 border rounded-lg text-[10px] font-bold transition-colors flex items-center justify-center gap-1 ${app.status === 'active' || app.status === 'enabled' ? 'border-red-100 text-red-500 hover:bg-red-50' : 'border-emerald-100 text-emerald-500 hover:bg-emerald-50'}`}>
                <SafeIcon icon={updatingId === app.id ? FiLoader : (app.status === 'active' || app.status === 'enabled' ? FiPauseCircle : FiPlayCircle)} className={updatingId === app.id ? 'animate-spin' : ''} />
                {app.status === 'active' || app.status === 'enabled' ? 'Suspend' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {showAddModal && <CreateAppModal clients={clients} onClose={() => setShowAddModal(false)} onSave={() => { setShowAddModal(false); fetchData(); showToast('Application created successfully'); }} />}
    </div>
  );
};

export default Apps;